import { useEffect, useState } from 'react';
import { Check, ChevronDown, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button, type ButtonProps } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import {
  MAIL_CLIENT_EVENT, MAIL_CLIENT_LABEL, dispatchEmail, emailButtonLabel, preferredMailClient,
  type Draft, type MailClient,
} from '@/lib/composeDispatch';
import { cn } from '@/lib/utils';

/** Live mail-client choice, shared across every open surface. */
export function useMailClient(): MailClient {
  const { user } = useAuth();
  const [c, setC] = useState<MailClient>(() => preferredMailClient(user?.email));
  useEffect(() => {
    const on = (e: Event) => setC((e as CustomEvent<MailClient>).detail);
    window.addEventListener(MAIL_CLIENT_EVENT, on);
    return () => window.removeEventListener(MAIL_CLIENT_EVENT, on);
  }, []);
  return c;
}

interface Props {
  draft: Draft;
  /** Override the label (e.g. "Email part 1 of 3"); receives the active client. */
  label?: (c: MailClient) => string;
  /** Runs after the draft opens (tracking, batching). */
  onSent?: (c: MailClient) => void;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  className?: string;
  'data-cue'?: string;
}

/**
 * The single email split-button: opens a draft in Gmail / Outlook / Mail app,
 * lets the person switch apps from the arrow, always copies, always toasts.
 */
export function MailClientButton({ draft, label, onSent, size, variant = 'default', className, ...rest }: Props) {
  const client = useMailClient();
  const send = (c: MailClient) => {
    const r = dispatchEmail(c, draft);
    toast.info(r.title, { description: r.description });
    onSent?.(c);
  };
  return (
    <div className={cn('inline-flex', className)}>
      <Button size={size} variant={variant} className="rounded-r-none" data-cue={rest['data-cue']} onClick={() => send(client)}>
        <Mail className="mr-1.5 h-4 w-4" />
        {label ? label(client) : emailButtonLabel(client)}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size={size} variant="outline" className="rounded-l-none -ml-px px-2" aria-label="Choose email app">
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {(Object.keys(MAIL_CLIENT_LABEL) as MailClient[]).map((c) => (
            <DropdownMenuItem key={c} onSelect={() => send(c)}>
              {c === client ? <Check className="mr-2 h-3.5 w-3.5" /> : <span className="mr-2 w-3.5" />}Open in {MAIL_CLIENT_LABEL[c]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default MailClientButton;
